import instance, { ApiResponse } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { BaseApi } from 'baseApi';
import { endpoints } from 'endpoints.config';
import { Package } from 'pages/newSubscription/models';
import {
  ISchoolDetails,
  ISchoolUser,
  ISchoolUserFreeTrial,
  SubscribeParser,
} from 'pages/subscribe/models';

const UNKOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class SubscribeApi extends BaseApi {
  public static getPackages = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.public.subscription.getSubscriptions
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  // get subscription packages
  public static getSubscriptionsWithPromocode = async (
    promoCode: string
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.public.subscription.getSubscriptionsWithPromocode +
          `?promoCode=${promoCode}`
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static validateSchoolInfo = async (
    values: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSchoolDetailsPayload(values);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.validateSchoolInfo,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static createSchoolUser = async (
    values: ISchoolUser,
    schoolDetails: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSchoolUserDetailsPayload(
        values,
        schoolDetails
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.createsSchoolUser,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static createSchoolUserFreeTrial = async (
    values: ISchoolUserFreeTrial,
    schoolDetails: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSchoolUserDetailsPayloadFreeTrial(
        values,
        schoolDetails
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.createsSchoolUserFreeTrial,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };  

  public static subscribeByInvoice = async (
    registrationToken: string,
    promoCode: string,
    packageIDs: Package[],
    expectedGrossAmount: number,
    poNumber: string,
    FinanceEmail: string,
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildArchiveNowPayload(
        registrationToken,
        promoCode,
        packageIDs,
        expectedGrossAmount,
        poNumber,
        FinanceEmail,
      );
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.subscribeByInvoice,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static subscribeByCard = async (
    promoCode: string,
    packages: Package[],
    total: number,
    token: any,
    registrationToken: string,
    poNumber: string,
    FinanceEmail: string,
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSubscribeByCardPayload(
        promoCode,
        packages,
        total,
        token,
        registrationToken,
        poNumber,
        FinanceEmail,
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.subscribeByCard,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateAfter3DSecure = async (values: any): Promise<any> => {
    try {
      const payload = SubscribeParser.buildUpdateAfter3DSecurePayload(values);
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.updateAfter3DSecure,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
}
